<template>
    <div class="tile-content">
        <div class="tile-subtitle">
            <h3>Order Item #{{orderItemIndex + 1 }}</h3>
            <v-icon-button
                class="no-flex no-padding"
                size="small"
                tooltip="Delete"
                type="basic"
                :disabled="isDisabled"
                @click="deleteOrderItem">
                <v-icon icon="trash"/>
            </v-icon-button>
        </div>
        <div class="grid-content">
            <div class="grid-item grid-item-half grid-item-break-half">
                <v-select
                    placeholder="Select your product"
                    label="Products"
                    option-label="value"
                    :auto-focus="!hasMultipleWebsites"
                    :options="productOptions"
                    :validate="validate.$productId"
                    :value="selectedProductId"
                    @input="selectProduct($event)"/>
            </div>
            <div v-if="selectedProduct" class="grid-item grid-content position-relative">
                <v-inline-loader v-if="isSelectingProduct"/>
                <div class="grid-item grid-item-full" v-if="!selectedPlan">
                    <p class="stack-s">Plan</p>
                    <v-icon-button
                        :class="{'is-error': validate.planId.$invalid}"
                        @click="openModal"
                        type="basic-outline"
                        :disabled="!planList.length"
                        fluid
                        size="large">
                        <v-icon slot="left-icon" icon="plus"/>
                        {{ addPlanLabel }}
                    </v-icon-button>
                </div>
                <div class="grid-item grid-item-full" v-else>
                    <p class="stack-s">Plan</p>
                    <div class="tile tile-secondary">
                        <div class="tile-content">
                            <plan-details-table
                                :plan="selectedPlan"
                                :product="selectedProduct"
                                :plans-custom-fields="plansCustomFieldsSchema"
                                hide-status>
                                <div slot="actions">
                                    <r-button class="no-flex" @click="openModal" type="link">Change Plan</r-button>
                                </div>
                            </plan-details-table>
                        </div>
                    </div>
                </div>
                <div class="grid-item grid-item-quarter">
                    <r-input
                        label="Quantity"
                        v-model="quantity"
                        :validate="validate.quantity"
                        @input="updateQuantity"
                        :right-label="selectedProduct.unitLabel"/>
                </div>
                <v-modal v-if="showModal" title="Add Plan" @close="closeModal" size="large">
                    <div slot="contents">
                        <div class="modal-content">
                            <r-input v-model="searchValue" placeholder="Search Plan" left-icon="search"/>
                        </div>
                        <div class="modal-content" :class="{'is-scrollable': planList.length > 6}">
                            <ul v-if="searchPlan.length" class="list list-group list-divided list-relaxed">
                                <li class="list-item" v-for="plan in searchPlan" :key="plan.id">
                                    <pricing-plan-radio
                                        @input="updatePlan"
                                        :selected="selectedPlan && selectedPlan.id === plan.id"
                                        :unitLabel="selectedProduct.unitLabel"
                                        :plan="plan"/>
                                </li>
                            </ul>
                            <p v-else class="text-center text-muted">No plans found</p>
                        </div>
                    </div>
                </v-modal>
            </div>
        </div>
    </div>
</template>
<script>
    import PlanModel from '@/common/models/plan-model';
    import {
        vIcon, vIconButton, vRadio, vSelect,
    } from '@/components/form';
    import vModal from '@/components/modal/v-modal';
    import vInlineLoader from '@/components/v-inline-loader';
    import PricingPlanRadio from './pricing-plan-radio';
    import PlanDetailsTable from '@/modules/plans/components/plan-details-table';

    export default {
        name: 'products-and-plans',
        props: {
            productList: {
                type: Array,
                default: [],
            },
            planList: {
                type: Array,
                default: () => [],
            },
            isDisabled: {
                type: Boolean,
            },
            orderItem: {
                type: Object,
            },
            orderItemIndex: {
                type: Number,
            },
            validate: {
                type: Object,
            },
            hasMultipleWebsites: {
                type: Boolean,
                default: false,
            },
        },
        components: {
            vIcon,
            vIconButton,
            vRadio,
            vSelect,
            vInlineLoader,
            vModal,
            'pricing-plan-radio': PricingPlanRadio,
            'plan-details-table': PlanDetailsTable,
        },
        data() {
            return {
                isSelectingProduct: false,
                selectedProduct: null,
                selectedPlan: null,
                quantity: this.orderItem.quantity,
                searchValue: '',
                showModal: false,
                plansCustomFieldsSchema: [],
            };
        },
        computed: {
            addPlanLabel() {
                return this.planList.length
                    ? 'Select a Plan'
                    : 'No plans available for this product';
            },
            searchPlan() {
                const searchValue = this.searchValue.toLowerCase();
                return searchValue
                    ? this.planList.filter(({name}) => name.toLowerCase().includes(searchValue))
                    : this.planList;
            },
            selectedProductId() {
                return this.selectedProduct && this.selectedProduct.id;
            },
            productOptions() {
                return this.productList.map(product => product.id);
            },
        },
        methods: {
            openModal() {
                this.showModal = true;
            },
            closeModal() {
                this.showModal = false;
            },
            deleteOrderItem() {
                this.$emit('delete-order-item', true);
            },
            updateOrderItem(product, plan, quantity) {
                this.selectedProduct = product;
                this.selectedPlan = plan;
                this.quantity = quantity;
                this.$emit('input', {product, plan, quantity});
            },
            updatePlan(plan) {
                // TODO: Check what $nextTick means and when it's useful.
                this.$nextTick(() => this.updateOrderItem(this.selectedProduct, plan, this.quantity));
                this.showModal = false;
            },
            updateQuantity(quantity) {
                this.updateOrderItem(this.selectedProduct, this.selectedPlan, quantity);
            },
            async selectProduct(productId) {
                this.isSelectingProduct = true;
                const selectedProduct = this.productList.find(product => product.id === productId);
                try {
                    const params = {
                        limit: 100,
                        filter: `productId:${selectedProduct.id}`,
                    };
                    const {items} = await this.$api(({plans}) => plans.getAll(params));
                    const planList = items.map(({fields}) => new PlanModel(fields));
                    // TODO: Check plan cache, it seems does not work.
                    this.$emit('plan-list', planList);
                } catch (error) {
                    this.$toast.fromError(error, 'Something went wrong, please contact support');
                } finally {
                    this.updateOrderItem(selectedProduct, null, this.quantity);
                    this.isSelectingProduct = false;
                }
            },
            async fetchData() {
                const [
                    {items: plansCustomFieldsSchema},
                ] = await Promise.all([
                    this.$api(({customFields}) => customFields.getAll({resource: 'plans'})),
                ]);
                this.plansCustomFieldsSchema = plansCustomFieldsSchema;
            },
        },
        created() {
            this.fetchData();
        },
    };
</script>
